import { useEffect, useRef, useState } from 'react';
import { CommunityUserProfile } from 'types/community';
import styles from './CommunityProfile.module.css';
import Avatar from 'components/common/Avatar';
import ProfileSocialLinks from './ProfileSocialLinks';
import { ValueCallback } from 'types/callbacks';
import { PinIcon } from 'components/common/svgIcons';
import UserPostsGrid from './UserPostsGrid';
import UserLikesGrid from './UserLikesGrid';
import RiveTabList, { RiveTab } from 'components/common/RiveTabList';
import featureFlags from 'lib/featureFlags';

type Props = {
  profile: CommunityUserProfile;
  onProfileVisibleChange: ValueCallback<boolean>;
};

export default function CommunityProfile({
  profile,
  onProfileVisibleChange,
}: Props) {
  const usernameRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState('posts');

  useEffect(() => {
    const callback: IntersectionObserverCallback = ([entry]) =>
      onProfileVisibleChange(entry.isIntersecting);

    const options = {
      rootMargin: '-80px',
    };

    const observer = new IntersectionObserver(callback, options);

    observer.observe(usernameRef.current!);

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.Container}>
      <div className={styles.ProfileContainer}>
        <div className={styles.UserSection}>
          <div className={styles.Avatar}>
            <Avatar data={profile} />
          </div>
          <div ref={usernameRef} className={styles.Username}>
            {profile.username}
          </div>
          <div className={styles.Blurb}>{profile.blurb}</div>
          <div className={styles.Location}>
            {profile.location && (
              <>
                <PinIcon width={20} height={20} />
                {profile.location}
              </>
            )}
            {profile.location && !!profile.isForHire && (
              <div className={styles.Separator}>•</div>
            )}
            {!!profile.isForHire && (
              <div className={styles.ForHireBadge}>For Hire</div>
            )}
          </div>
          <ProfileSocialLinks profile={profile} />
        </div>
        {featureFlags.userLikes() && (
          <div className={styles.TabList}>
            <RiveTabList
              selectedTab={selectedTab}
              onTabSelect={(value) => setSelectedTab(value)}
              fitContentWidth={false}
            >
              <RiveTab value="posts">Posts</RiveTab>
              <RiveTab value="likes">Likes</RiveTab>
            </RiveTabList>
          </div>
        )}
        <div className={styles.GridSection}>
          {selectedTab === 'posts' ? (
            <UserPostsGrid profile={profile} />
          ) : (
            <UserLikesGrid profile={profile} />
          )}
        </div>
      </div>
    </div>
  );
}
